import { TradeTapeItem } from '@paradigm/features/src/unified-rfqs/entities/shared';

/**
 * Format a UnifiedRfqs trade-tape row as a chat prompt for the agent.
 *
 * Strategy: dump the row as JSON (so the agent has every field — legs,
 * ratios, sides, prices, ts) plus a short instruction in plain English so
 * the LLM knows what to do with it. The system preamble passed by
 * BlockTerminalTab already tells the agent the surrounding context
 * (paradigm options, RFQ flow, etc.) so we don't repeat that here.
 */
export function buildTradeAnalysisPrompt(trade: TradeTapeItem): string {
  const json = JSON.stringify(serializeTrade(trade), null, 2);
  return [
    'Analyze this Paradigm block trade. Walk through the structure, the',
    "expected risk profile (Greeks if it's an option spread), what view it",
    'expresses, and any notable size or pricing details:',
    '',
    '```json',
    json,
    '```',
  ].join('\n');
}

/**
 * Strip the trade row down to JSON-serializable fields. The raw
 * `TradeTapeItem` is a discriminated union with Date instances and
 * sometimes class instances on it — `JSON.stringify` swallows those
 * silently and the agent ends up with a useless `{}` payload, so do the
 * conversion explicitly.
 */
function serializeTrade(trade: TradeTapeItem): Record<string, unknown> {
  const base = JSON.parse(JSON.stringify(trade)) as Record<string, unknown>;

  // Both branches expose a tape time but on different field names; surface
  // a unified `traded_at` ISO string so the agent doesn't have to reason
  // about the union shape.
  if (trade.rfqType === 'grfq') {
    base.traded_at = trade.traded.toISOString();
  } else if (trade.rfqType === 'drfq') {
    base.traded_at = trade.filled_at.toISOString();
  }

  return base;
}
